import type { DeliverableView, TaskView } from "@fieldpath/shared";
import { mdyDay } from "@fieldpath/shared";
import { useState } from "react";

import { navigate } from "../router";
import { KindTag } from "./KindTag";

/**
 * Cancelled tasks, in a section of their own (punch list #28, Jeremy 16 Oct
 * 2026).
 *
 * They used to sit among the live work showing the status they had reached
 * -- usually "Not Started" -- with nothing saying they were cancelled, so a
 * dead task read as work nobody had begun. A cancelled task keeps that status
 * because it is true (the work got that far); what it must not do is look
 * live. So: apart from the work, shut until asked for, and each row says
 * CANCELLED where the status would be, with the reason beside it.
 *
 * Read-only, like the rows it replaces. Reinstating is on the task.
 */
export function CancelledTasks({
  tasks,
  deliverables = [],
  deliverableNames,
}: {
  tasks: readonly TaskView[];
  /**
   * Cancelled deliverables, listed first (punch list #32/#34, 20 Oct 2026).
   * A deliverable can only be cancelled when empty, so it carries no live
   * tasks; any cancelled ones are among `tasks`, saying where they came from.
   */
  deliverables?: readonly DeliverableView[];
  /** Deliverable id to name, to say where each came from on the project page. */
  deliverableNames?: ReadonlyMap<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const count = tasks.length + deliverables.length;
  if (count === 0) return null;

  return (
    <section className="cancelled-group">
      <button className="section-label cancelled-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>
          <span className="caret" aria-hidden="true">{open ? "▾" : "▸"}</span> Cancelled
        </span>
        <span className="count">{count}</span>
      </button>
      {open
        ? deliverables.map((d) => (
            <button
              key={d.id}
              className="trow trow-cancelled"
              onClick={() => navigate({ kind: "deliverable", id: d.id })}
            >
              <span className="trow-main">
                <KindTag kind="deliverable" size="row" />
                <span className="trow-title">{d.name}</span>
              </span>
              <span className="trow-who">{d.typeName ?? ""}</span>
              <span className="chip cancelled-mark">CANCELLED</span>
              <span className="trow-due">
                {d.cancelledAt ? mdyDay(d.cancelledAt) : "—"}
              </span>
            </button>
          ))
        : null}
      {open
        ? tasks.map((t) => {
            const from = t.deliverableId ? deliverableNames?.get(t.deliverableId) : undefined;
            return (
              <button
                key={t.id}
                className="trow trow-cancelled"
                onClick={() => navigate({ kind: "task", id: t.id })}
              >
                <span className="trow-main">
                  <KindTag kind="task" size="row" />
                  <span className="trow-title">{t.title}</span>
                  {from ? <span className="trow-from">from {from}</span> : null}
                </span>
                <span className="trow-who" title={t.cancelReason ?? undefined}>
                  {t.cancelReason ?? ""}
                </span>
                <span className="chip cancelled-mark">CANCELLED</span>
                <span className="trow-due">
                  {/* The day in the READER's timezone, written like the due
                      dates in this column. */}
                  {t.cancelledAt ? mdyDay(t.cancelledAt) : "—"}
                </span>
              </button>
            );
          })
        : null}
    </section>
  );
}
